import React from "react";
import { Combobox } from "@headlessui/react";
import { Check } from "lucide-react";
import { AutoCompleteOption } from "./types";
import LoadingSpinner from "../LoadingSpinner";

interface AutoCompleteContentProps {
  filteredItems: AutoCompleteOption[];
  query: string;
  addNewOption?: boolean;
  isLoading?: boolean;
  getAddOptionText?: (query: string) => string;
  renderOption?: (option: AutoCompleteOption) => React.ReactNode;
}

export const AutoCompleteContent = ({
  filteredItems,
  query,
  addNewOption,
  isLoading,
  getAddOptionText,
  renderOption,
}: AutoCompleteContentProps) => {
  if (isLoading) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <LoadingSpinner size={4} />
      </div>
    );
  }

  if (filteredItems.length === 0 && query === "") {
    return (
      <div className="relative py-2 pl-3 pr-4 text-gray-500">
        No matches found
      </div>
    );
  }

  if (filteredItems.length === 0 && query !== "") {
    if (addNewOption) {
      return (
        <Combobox.Option
          className="relative cursor-pointer select-none py-2 pl-3 pr-4 text-gray-900 transition-colors hover:bg-gray-50"
          value={{ id: query, value: query, label: query }}
        >
          {getAddOptionText?.(query) ||
            `Would you like to add ${query} as a new option?`}
        </Combobox.Option>
      );
    }

    return (
      <div className="relative py-2 pl-3 pr-4 text-gray-500">
        No matches found for "{query}".
      </div>
    );
  }

  return (
    <>
      {filteredItems?.map((item: AutoCompleteOption, index: number) => (
        <Combobox.Option
          key={`${item.label}-${index}`}
          value={item}
          className={({ active }) =>
            `relative mx-1 cursor-pointer select-none rounded-md py-2 pl-3 pr-9 transition-colors ${
              active ? "bg-gray-100 text-gray-900" : "text-gray-900"
            }`
          }
        >
          {({ active, selected }) => (
            <>
              <span
                className={`flex items-center truncate ${
                  selected ? "font-medium" : "font-normal"
                }`}
              >
                {renderOption?.(item) || item?.label}
              </span>
              {selected && (
                <span
                  className={`absolute inset-y-0 right-0 flex items-center pr-4 ${
                    active ? "text-blue-600" : "text-blue-500"
                  }`}
                >
                  <Check className="h-5 w-5" />
                </span>
              )}
            </>
          )}
        </Combobox.Option>
      ))}
    </>
  );
};
